import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import blank from '../../../assets/blank.png';
import { useAuth } from '../../../hooks/AuthContext';
import DropdownMenu from '../DropdownMenu';
import DropdownNotification from '../DropdownNotification';
import DropdownConfigs from '../DropdownConfigs';
import DropdownProfile from '../DropdownProfile';
import MobileNav from './MobileNav';

const DEVYX_LOGO_URL = 'https://cdn.devyx.com.br/assets/logo/devyx/dark.png';

const getInitials = (name?: string): string => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const Header: React.FC = () => {
  const { pathname } = useLocation();
  const { authData } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const avatarURL = `${process.env.REACT_APP_AVATAR_URL}/${authData.keycloackId}_mini.png?${authData.updated_at}`;

  const isActive = (path: string) => {
    if (path === '/') {
      return pathname === path;
    }
    return pathname.startsWith(path);
  };

  const navItems = [
    { label: 'Dashboard', path: '/' },
    { label: 'Usuários', path: '/users' },
    { label: 'Companhias', path: '/companies' },
    { label: 'Aprovações', path: '/admin/plan-approvals' },
  ];

  return (
    <header
      className="sticky top-0 z-50 w-full border-b backdrop-blur supports-[backdrop-filter]:bg-background/60"
      style={{
        backgroundColor: 'var(--neutral-off-white)',
        borderColor: 'var(--neutral-beige)',
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center transition-opacity hover:opacity-80"
            >
              <img
                src={DEVYX_LOGO_URL}
                alt="Devyx"
                className="h-10 w-auto max-w-[180px] object-contain"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                  )}
                  style={{
                    backgroundColor: isActive(item.path)
                      ? 'var(--neutral-beige)'
                      : 'transparent',
                    color: isActive(item.path)
                      ? 'var(--primary-dark)'
                      : 'var(--neutral-grey)',
                  }}
                  onMouseEnter={e => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor =
                        'var(--neutral-beige)';
                      e.currentTarget.style.color = 'var(--primary-dark)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--neutral-grey)';
                    }
                  }}
                >
                  {item.label}
                </Link>
              ))}

              <DropdownMenu pathname={pathname} />
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <DropdownNotification />
            </div>

            <div className="hidden lg:block">
              <DropdownConfigs />
            </div>

            <div className="hidden md:block">
              <DropdownProfile
                user={{
                  name: authData.name,
                  email: authData.email,
                  avatar: authData.avatar,
                  avatarURL,
                }}
                toggle={
                  <Button variant="ghost" className="gap-2 px-2">
                    <span className="hidden lg:inline-block text-sm font-medium">
                      {authData.name || 'Usuário'}
                    </span>
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={authData.avatar ? avatarURL : blank}
                        alt={authData.name || 'Usuário'}
                      />
                      <AvatarFallback>
                        {getInitials(authData.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                }
              />
            </div>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0">
                <MobileNav
                  pathname={pathname}
                  user={{
                    name: authData.name,
                    email: authData.email,
                    avatar: authData.avatar,
                    avatarURL,
                  }}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
